"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/contexts/AuthContext";
import { AuthProvider } from "@/contexts/AuthContext";
import LoginForm from "./components/LoginForm";
import LoginHeader from "./components/LoginHeader";
import LoginFooter from "./components/LoginFooter";

function LoginLoadingBlock({ message }: { message: string }) {
  return (
    <div
      className="animate-login-in relative flex w-full max-w-md flex-col items-center justify-center rounded-xl bg-white/95 px-8 py-16 shadow-lg backdrop-blur-sm sm:px-10"
      role="status"
      aria-live="polite"
    >
      <span
        className="h-10 w-10 shrink-0 animate-spin rounded-full border-2 border-amber-primary/25 border-t-amber-primary"
        aria-hidden
      />
      <p className="mt-5 text-center text-sm font-medium text-gray-700">{message}</p>
      <p className="mt-1 text-center text-xs text-gray-500">Just a moment</p>
    </div>
  );
}

function LoginSubmitOverlay({ message }: { message: string }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/25 px-4 backdrop-blur-[2px]"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="animate-login-in flex w-full max-w-sm flex-col items-center rounded-xl bg-white px-8 py-10 shadow-xl">
        <span
          className="h-10 w-10 shrink-0 animate-spin rounded-full border-2 border-amber-primary/25 border-t-amber-primary"
          aria-hidden
        />
        <p className="mt-5 text-center text-sm font-medium text-gray-800">{message}</p>
        <p className="mt-1 text-center text-xs text-gray-500">Just a moment</p>
      </div>
    </div>
  );
}

function LoginPage() {
  const router = useRouter();
  const { login, isAuthenticated, isLoading } = useAuth();
  const [loginSubmitting, setLoginSubmitting] = useState(false);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.has("identifier") || url.searchParams.has("password")) {
      url.search = "";
      window.history.replaceState({}, "", `${url.pathname}${url.hash}`);
    }
  }, []);

  // Trava a rolagem do documento enquanto a tela de login está montada: o body
  // global usa min-h-screen (100vh = large viewport), maior que a área visível
  // no mobile, o que criava uma barra de rolagem vertical sem conteúdo que a
  // justificasse. O conteúdo do login cabe na viewport e não precisa rolar.
  useEffect(() => {
    const { documentElement, body } = document;
    const prevHtml = documentElement.style.overflow;
    const prevBody = body.style.overflow;
    documentElement.style.overflow = "hidden";
    body.style.overflow = "hidden";
    return () => {
      documentElement.style.overflow = prevHtml;
      body.style.overflow = prevBody;
    };
  }, []);

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      router.replace("/dashboards/home");
    }
  }, [isAuthenticated, isLoading, router]);

  const handleLogin = async (identifier: string, password: string) => {
    setLoginSubmitting(true);
    try {
      await login(identifier, password);
      router.push("/dashboards/home");
    } finally {
      setLoginSubmitting(false);
    }
  };

  const sessionChecking = isLoading;
  const pendingRedirect = !isLoading && isAuthenticated;
  const showLoginShell = !sessionChecking && !pendingRedirect;

  return (
    <div className="relative flex h-dvh w-full flex-col items-center justify-center overflow-y-auto px-4 py-8">
      {/* Fundo fixo cobre sempre a viewport visível, evitando que o bg do body
          (min-h-screen/100vh) vaze como uma faixa abaixo do gradiente no mobile. */}
      <div
        aria-hidden
        className="fixed inset-0 -z-10 bg-gradient-to-br from-amber-sidebar to-amber-primary"
      />
      <LoginHeader />
      {sessionChecking && <LoginLoadingBlock message="Checking your session…" />}
      {pendingRedirect && <LoginLoadingBlock message="Opening your workspace…" />}
      {showLoginShell && (
        <>
          {loginSubmitting && <LoginSubmitOverlay message="Signing in…" />}
          <div className="animate-login-in w-full max-w-md">
            <LoginForm onSubmit={handleLogin} />
          </div>
        </>
      )}
      <LoginFooter />
    </div>
  );
}

export default function LoginPageWrapper() {
  return (
    <AuthProvider>
      <LoginPage />
    </AuthProvider>
  );
}
